import React, { Dispatch, SetStateAction, useMemo, useRef } from 'react';
import {
  ComponentName,
  DefaultSortingOptionsForProducts,
  getSaleTypeTrackingValue,
  RetailerKpiOption,
  RetailerKpiOptionDisplayName,
  SaleType,
  TopSellingProductsChartId,
} from '@models/Products';
import {
  ChartElementName,
  ChartElementType,
  chartInteractionTrackingFunction,
  ChartInteractionTrackingFunctionParam,
  ChartInteractionTrackingFunctionParamSubset,
  RetailerClientStateKey,
  useGenerateChartInteractionTrackingParam,
} from '@utils/page-analytics/model/ChartInteractionTracking';
import { PERFORMANCE_TREND_TITLE } from '@pages/Utilities';
import { capitalize } from 'lodash';
import { FactRevenue } from '@utils/pageConfig/Types';
import { constructCurrencyLabel } from '@utils/currencyUtils/CurrencyUtils';
import { DEFAULT_VOLUME } from '@utils/factVolumeUtils/FactVolumeUtils';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { useExportImages } from '@hooks/export/UseExportChartImage';
import { WithHiddenListBox } from '@components/design';
import { ExportExcelProps } from '@components/products-line-charts/Types';
import ProductsCompareModalHeaderIcon from '@components/products-compare-modal/products-compare-modal-table/header-icon/ProductsCompareModalHeaderIcon';
import ProductTrendsHeaderIcon from '@pages/product-analyzer/trends/ProductTrendsHeaderIcon';
import { containers } from '@ecosystem/newron-design-system';
import { PerformanceAnalysisLineChartInsight } from '@components/collections/performance-analysis-line-chart/Model';
import { CreateInsightDataFn } from '@ecosystem/newron-client-collections';
import { ProductsViewState } from '@models/SavedViews';
import usePersistedSorting from '@utils/hook/sorting/UsePersistedSorting';
import { getHeaderKey } from '@data-visualization/utilities/ChartUtils';

interface Props {
  selectedLineChartSaleType: SaleType;
  setSelectedLineChartSaleType: Dispatch<SetStateAction<SaleType>>;
  selectedShareOption: RetailerKpiOption;
  setSelectedShareOption: Dispatch<SetStateAction<RetailerKpiOption>>;
  onShowHandler?: (value: string) => void;
  onShareHandler?: (value: string) => void;
  tooltipComponent?: JSX.Element;
  factRevenue: FactRevenue;
  volume?: string;
  retailerClientStateKey?: RetailerClientStateKey;
  isLoading: boolean;
  excludeFilter?: boolean;
  marketExportExcelParam?: ExportExcelProps;
  retailerExportExcelParam?: ExportExcelProps;
  createInsightDataFn: CreateInsightDataFn<PerformanceAnalysisLineChartInsight>;
  productsViewState: ProductsViewState;
}

const ProductsChartsFilter: React.FC<Props> = ({
  selectedLineChartSaleType,
  setSelectedLineChartSaleType,
  selectedShareOption,
  setSelectedShareOption,
  onShowHandler,
  onShareHandler,
  tooltipComponent,
  factRevenue,
  volume = DEFAULT_VOLUME,
  retailerClientStateKey = 'hitlistsRetailerClient',
  isLoading,
  excludeFilter = false,
  marketExportExcelParam,
  retailerExportExcelParam,
  createInsightDataFn,
  productsViewState,
}) => {
  const chartInteractionParam = useGenerateChartInteractionTrackingParam(retailerClientStateKey);
  const currencyLabel = constructCurrencyLabel(factRevenue);
  const { access, pageType } = usePageConfig();

  const { clearExportImage } = useExportImages(pageType);

  const dispSaleType = useRef(selectedLineChartSaleType);

  const retailerShareOptionRef = useRef(selectedShareOption);
const { sorting, updateOptions } = usePersistedSorting({
    tableId: TopSellingProductsChartId,
    defaultSorting: DefaultSortingOptionsForProducts,
  });
  const { Card } = containers;

  const showOptions = [
    {
      label: `Revenue${currencyLabel}`,
      value: SaleType.revenue,
    },
    {
      label: volume,
      value: SaleType.volume,
    },
    {
      label: `Price${currencyLabel}`,
      value: SaleType.price,
    },
  ];

  const shareOptions = [
    {
      label: `Absolute`,
      value: RetailerKpiOption.absolute,
    },
    {
      label: `Retailer ${RetailerKpiOptionDisplayName[RetailerKpiOption.share]}`,
      value: RetailerKpiOption.share,
    },
    {
      label: `Retailer ${RetailerKpiOptionDisplayName[RetailerKpiOption.mix]}`,
      value: RetailerKpiOption.mix,
    },
  ];

  const marketChartInteractionTracking = (
    val: string,
    chartInteractionTrackingFunctionParamSubset: ChartInteractionTrackingFunctionParamSubset,
  ) => {
    //Start Chart Interaction Tracking

    const customChartInteractionParam = {
      ...chartInteractionTrackingFunctionParamSubset,
      optionChosen: capitalize(val),
      chartName: PERFORMANCE_TREND_TITLE,
      elementName: ChartElementName.SHOW,
      elementType: ChartElementType.RADIO,
      access: access,
    } as ChartInteractionTrackingFunctionParam;

    chartInteractionTrackingFunction(customChartInteractionParam);
    //End Chart Interaction Tracking
  };

  const retailerChartInteractionTracking = (
    val: string,
    chartInteractionTrackingFunctionParamSubset: ChartInteractionTrackingFunctionParamSubset,
  ) => {
    //Start Chart Interaction Tracking
    const retailerLineChartInteractionParam = {
      ...chartInteractionTrackingFunctionParamSubset,
      optionChosen: capitalize(val),
      chartName: ComponentName.PRODUCT_TRENDS_RETAILER_LINE_CHART,
      elementName: ChartElementName.SHOW,
      elementType: ChartElementType.RADIO,
      access: access,
    } as ChartInteractionTrackingFunctionParam;

    chartInteractionTrackingFunction(retailerLineChartInteractionParam);
    //End Chart Interaction Tracking
  };

  const getOptionLabel = (availableOptions: any[], value: string) => {
    const option = availableOptions.find((opt) => opt.value === value);
    return option ? option.label : null;
  };

  const memoizedSelectedShowLabelOption = useMemo(
    () => getOptionLabel(showOptions, selectedLineChartSaleType),
    [showOptions, selectedLineChartSaleType],
  );

  const memoizedSelectedShareLabelOption = useMemo(
    () => getOptionLabel(shareOptions, selectedShareOption),
    [showOptions, selectedLineChartSaleType],
  );

  const getProductsTrendsHeaderIcon = () => {
    return (
      marketExportExcelParam && (
        <ProductTrendsHeaderIcon
          isExportButtonDisabled={marketExportExcelParam.isExportButtonDisabled}
          onChartDownload={marketExportExcelParam.onChartDownload}
          createInsightDataFn={createInsightDataFn}
          productsViewState={productsViewState}
        />
      )
    );
  };

  const getCompareProductsHeaderIcon = () => {
    return retailerExportExcelParam ? (
      <ProductsCompareModalHeaderIcon
        selectedSalesType={selectedLineChartSaleType}
        isExportButtonDisabled={retailerExportExcelParam.isExportButtonDisabled}
        onChartDownload={retailerExportExcelParam.onChartDownload}
      />
    ) : (
      <div></div>
    );
  };

  return (
    <>
      {!excludeFilter && (
        <Card.Title title={' '}>
          <div data-testid="products-market-filter" className={'flex'}>
            <Card.Content>
              <WithHiddenListBox
                isDisabled={isLoading}
                label="Show"
                options={showOptions}
                value={memoizedSelectedShowLabelOption}
                onChange={({ value }) => {
                  if (dispSaleType.current !== value) {
                    dispSaleType.current = value as SaleType;
                    onShowHandler?.(value);
                    setSelectedLineChartSaleType(value as SaleType);
                    clearExportImage();
                    const newSaleType = SaleType[value as keyof typeof SaleType];
                        updateOptions({
                          ...sorting,
                          headerKey: getHeaderKey(newSaleType),
                        });
                    if (value === SaleType.price) {
                      //if Sale Type is Price, then display Retailer KPI options as Absolute.
                      setSelectedShareOption(RetailerKpiOption.absolute);
                      retailerShareOptionRef.current = RetailerKpiOption.absolute;
                    }
                  }
                  marketChartInteractionTracking(
                    getSaleTypeTrackingValue(volume)[value as SaleType],
                    chartInteractionParam,
                  );
                }}
              />
              <WithHiddenListBox
                isDisabled={selectedLineChartSaleType === SaleType.price || isLoading}
                label="and"
                options={shareOptions}
                value={memoizedSelectedShareLabelOption}
                onChange={({ value }) => {
                  if (retailerShareOptionRef.current !== value) {
                    retailerShareOptionRef.current = value as RetailerKpiOption;
                    onShareHandler?.(value);
                    setSelectedShareOption(value as RetailerKpiOption);
                    // clearExportImage(); // Need?
                  }
                  retailerChartInteractionTracking(value, chartInteractionParam);
                }}
              />
              {tooltipComponent}
            </Card.Content>
          </div>
          <Card.Title.Actions>
            {getProductsTrendsHeaderIcon()}
            {getCompareProductsHeaderIcon()}
          </Card.Title.Actions>
        </Card.Title>
      )}
    </>
  );
};

export default ProductsChartsFilter;
